import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, MapPin, UserPlus, MessageSquare, Filter, Building2, Trophy, Users, Heart, Briefcase, Camera } from 'lucide-react';
import { AZURE } from '../services/api';
import { getSwimmers } from '../services/swimmingService';
import { FlagImage } from '../helpers/flagMapper';
import PageHeader from '../components/layout/PageHeader';
import { BRAND_GREEN, BRAND_GOLD } from '../theme/sucronTheme';
import { useSportsLang } from '../contexts/SportsLangContext';


// --- CORES DA MARCA ---
const BORDER = '#e5e7eb';

const LABELS = {
  pt: {
    pageTitle: 'Pessoas — Arena Stats',
    pageSubtitle: 'Atletas e comunidade (clubes, técnicos, performance, business)',
    pageBadge: 'PESSOAS',
    searchPlaceholder: 'Buscar por nome, cargo, clube...',
    allSports: 'Todos os Esportes',
    swimming: '🏄 Natação',
    football: '⚽ Futebol',
    radarTitle: '🎯 Radar — Descoberta',
    radarTip: '💡 Dica: Use os filtros acima para explorar profissionais e construir sua rede',
    tabAll: 'Todos',
    tabField: 'Campo & Piscina',
    tabPerformance: 'Performance',
    tabBusiness: 'Business',
    tabMedia: 'Mídia',
    foundCount: (n) => `Encontramos ${n} perfis correspondentes`,
    emptyTitle: 'Nenhum perfil encontrado',
    emptyDesc: 'Tente ajustar sua busca ou os filtros.',
    clearFilters: 'Limpar filtros',
    following: 'Seguindo',
    requested: 'Solicitado',
    follow: 'Acompanhar',
    noClub: 'Sem clube',
    verified: '✓ VERIFICADO',
    badgeFollowing: 'SEGUINDO',
    badgeRequested: 'SOLICITADO',
  },
  en: {
    pageTitle: 'People — Arena Stats',
    pageSubtitle: 'Athletes and community (clubs, coaches, performance, business)',
    pageBadge: 'PEOPLE',
    searchPlaceholder: 'Search by name, role, club...',
    allSports: 'All Sports',
    swimming: '🏄 Swimming',
    football: '⚽ Football',
    radarTitle: '🎯 Radar — Discovery',
    radarTip: '💡 Tip: Use the filters above to explore professionals and build your network',
    tabAll: 'All',
    tabField: 'Field & Pool',
    tabPerformance: 'Performance',
    tabBusiness: 'Business',
    tabMedia: 'Media',
    foundCount: (n) => `Found ${n} matching profiles`,
    emptyTitle: 'No profiles found',
    emptyDesc: 'Try adjusting your search or filters.',
    clearFilters: 'Clear filters',
    following: 'Following',
    requested: 'Requested',
    follow: 'Follow',
    noClub: 'No club',
    verified: '✓ VERIFIED',
    badgeFollowing: 'FOLLOWING',
    badgeRequested: 'REQUESTED',
  },
};

// UI Primitives (padrão do site)
const PageShell = ({ children }) => (
  <div className="min-h-screen" style={{ backgroundColor: '#f9fafb' }}>
    <div className="max-w-7xl mx-auto px-4 lg:px-6 py-6">
      {children}
    </div>
  </div>
);

// --- COMPONENTE: CARD DE PESSOA (Estilo Radar/Social) ---
const PersonCard = ({ person }) => {
  const navigate = useNavigate();
  const { lang } = useSportsLang();
  const t = LABELS[lang] || LABELS.pt;

  const handleProfileClick = () => {
    // 🔒 Routing baseado em entity_kind (estável)
    // Garante compatibilidade com rotas reais do App.js
    switch (person.entity_kind) {
      case 'athlete_swimming':
        navigate(`/nadador/${person.id}`); // ✅ Route exists: /nadador/:id
        break;
      case 'athlete_football':
        navigate(`/jogador/${person.id}`); // ✅ Route exists: /jogador/:id
        break;
      case 'community_user':
        navigate(`/usuario/${person.id}`); // ✅ Route exists: /usuario/:userId
        break;
      default:
        // Fallback para evitar crash
        console.warn(`[PeopleDirectory] Unknown entity_kind: ${person.entity_kind}`);
        navigate(`/usuario/${person.id}`);
    }
  };

  const handleFollowAction = (e) => {
    e.stopPropagation();
    // TODO: Implementar follow/unfollow quando backend estiver pronto
    console.log('Follow action:', person.id, person.followStatus);
  };

  // Estados de follow (NONE, FOLLOWING, PENDING)
  const followStatus = person.followStatus || 'NONE';
  const isFollowing = followStatus === 'FOLLOWING';
  const isPending = followStatus === 'PENDING';

  return (
    <div 
      onClick={handleProfileClick}
      className={`bg-white rounded-xl border overflow-hidden hover:shadow-lg transition-all duration-300 group flex flex-col h-full relative cursor-pointer ${
        isFollowing ? 'border-green-100' : 'border-gray-200'
      }`}
      style={{ backgroundColor: isFollowing ? 'rgba(16, 185, 129, 0.02)' : 'white' }}
    >
      {/* Badge Status (Top-left) */}
      {(isFollowing || isPending) && (
        <div className="absolute top-3 left-3 z-20">
          <span className={`text-xs font-bold px-2 py-1 rounded ${
            isFollowing 
              ? 'bg-green-100 text-green-700' 
              : 'bg-yellow-100 text-yellow-700'
          }`}>
            {isFollowing ? t.badgeFollowing : t.badgeRequested}
          </span>
        </div>
      )}

      {/* Badge Verificado/Oficial (Top-right) */}
      {person.badges?.includes('VERIFICADO') && (
        <div className="absolute top-3 right-3 z-20">
          <span className="text-xs font-bold px-2 py-1 rounded bg-blue-100 text-blue-700">
            {t.verified}
          </span>
        </div>
      )}

      {/* 1. Capa do Perfil (Banner) */}
      <div className="h-20 bg-gradient-to-r from-gray-100 to-gray-200 relative">
        {person.teamLogo && (
          <div className="absolute top-2 right-2 bg-white/90 p-1.5 rounded-lg shadow-sm backdrop-blur-sm">
             <img 
               src={person.teamLogo} 
               alt={person.team} 
               className="w-6 h-6 object-contain"
               onError={(e) => e.target.style.display = 'none'}
             />
          </div>
        )}
      </div>

      {/* 2. Avatar e Info Principal */}
      <div className="px-4 pb-4 flex-1 flex flex-col items-center -mt-10 text-center relative z-10">
        <div 
            onClick={handleProfileClick}
            className="w-20 h-20 rounded-full border-4 border-white bg-white shadow-sm overflow-hidden cursor-pointer hover:scale-105 transition-transform"
        >
          {person.avatar ? (
            <img src={person.avatar} alt={person.name} className="w-full h-full object-cover" />
          ) : (
            <div className="w-full h-full bg-gray-100 flex items-center justify-center text-2xl font-bold text-gray-400">
              {person.name.charAt(0)}
            </div>
          )}
        </div>

        <div className="mt-3 w-full">
          <h3 
            onClick={handleProfileClick}
            className="font-bold text-gray-900 text-lg hover:text-[#111111] hover:underline cursor-pointer truncate"
          >
            {person.name}
          </h3>
          
          <p className="text-sm font-medium text-[#111111] mb-1 truncate">
            {person.role || person.position}
          </p>
          
          <p className="text-xs text-gray-500 mb-3 flex items-center justify-center gap-1">
             {person.sport === 'Natação' ? '🏊' : person.sport === 'Futebol' ? '⚽' : '🏅'} 
             {person.team || t.noClub}
          </p>

          {/* Localização / Nacionalidade */}
          <div className="flex items-center justify-center gap-1 text-xs text-gray-400 mb-2">
             <MapPin size={12} />
             <span>{person.location || person.nationality || 'Global'}</span>
          </div>

          {/* Contexto de Conexão */}
          {person.connectionContext && (
            <p className="text-xs text-gray-500 italic mb-4">
              {person.connectionContext}
            </p>
          )}
        </div>

        {/* 3. Botões de Ação (Footer) */}
        <div className="mt-auto w-full pt-4 border-t border-gray-100 flex gap-2">
           <button 
             onClick={handleFollowAction}
             className={`flex-1 flex items-center justify-center gap-2 py-1.5 rounded-lg text-xs font-bold transition-colors ${
               isFollowing 
                 ? 'border border-gray-300 text-gray-600 hover:bg-gray-50' 
                 : isPending
                 ? 'border border-yellow-400 text-yellow-700 bg-yellow-50'
                 : 'border border-[#111111] text-[#111111] hover:bg-green-50'
             }`}
           >
              <UserPlus size={14} />
              {isFollowing ? t.following : isPending ? t.requested : t.follow}
           </button>
           <button className="flex items-center justify-center px-3 py-1.5 rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50 transition-colors">
              <MessageSquare size={14} />
           </button>
        </div>
      </div>
    </div>
  );
};

// --- COMPONENTE PRINCIPAL ---
const PeopleDirectory = () => {
  const { lang } = useSportsLang();
  const t = LABELS[lang] || LABELS.pt;
  const [activeTab, setActiveTab] = useState('ALL'); // ALL, ATHLETES, COACHES
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedSport, setSelectedSport] = useState('ALL'); // ALL, SOCCER, SWIMMING
  
  const [loading, setLoading] = useState(true);
  const [people, setPeople] = useState([]);

  // --- BUSCAR DADOS (Normalização) ---
  useEffect(() => {
    const fetchAllData = async () => {
      setLoading(true);
      try {
        const allPeople = [];

        // 1. Buscar Nadadores
        try {
          const swimResponse = await getSwimmers({ limit: 200, offset: 0 });
          const swimmers = Array.isArray(swimResponse)
            ? swimResponse
            : (swimResponse?.items || swimResponse?.swimmers || swimResponse?.data || []);
          
          const normalizedSwimmers = swimmers.map(s => ({
            id: s.id,
            name: s.display_name || s.name,
            role: 'Atleta de Natação',
            position: 'Natação',
            sport: 'Natação',
            team: s.clubs?.[0]?.name || 'Sem clube oficial',
            teamLogo: s.clubs?.[0]?.logo_url,
            nationality: s.nationality,
            location: s.nationality,
            avatar: s.photo_url || null,
            type: 'ATHLETE',
            category: 'FIELD_POOL', // Campo & Piscina
            entity_kind: 'athlete_swimming', // 🔒 Routing explícito
            followStatus: 'NONE', // TODO: vir do backend
            badges: s.is_verified ? ['VERIFICADO'] : []
          }));
          allPeople.push(...normalizedSwimmers);
        } catch (e) { console.error("Erro ao buscar nadadores", e); }

        // 2. Buscar Jogadores de Futebol (usando novo endpoint /directory)
        try {
          const footballResponse = await AZURE.get('/football/players/directory', {
            params: { limit: 200 } 
          });
          const playersRaw = footballResponse.data?.items || footballResponse.data?.players || footballResponse.data?.data || footballResponse.data || [];
          const players = Array.isArray(playersRaw) ? playersRaw : [];

          const normalizedPlayers = players.map(p => ({
            id: p.player_id || p.id,
            name: p.full_name || p.name,
            role: `Jogador de Futebol • ${p.position_primary || p.position || 'Atleta'}`,
            position: p.position_primary || p.position,
            sport: 'Futebol',
            team: p.team_name || p.current_club || p.team,
            teamLogo: p.team_logo || p.teamLogo,
            nationality: p.country_code || p.nationality,
            location: p.country_code || 'Brasil',
            avatar: p.photo_url || p.photo || null,
            type: 'ATHLETE',
            category: 'FIELD_POOL',
            entity_kind: 'athlete_football', // 🔒 Routing explícito
            followStatus: 'NONE',
            badges: [],
            // Estatísticas adicionais
            totalAppearances: p.total_appearances,
            totalGoals: p.total_goals
          }));
          allPeople.push(...normalizedPlayers);
        } catch (e) { console.error("Erro ao buscar jogadores", e); }

        // 3. Buscar Usuários Cadastrados (Staff, Técnicos, etc.)
        try {
          const usersResponse = await AZURE.get('/users/directory');
          const users = Array.isArray(usersResponse.data) ? usersResponse.data : (usersResponse.data?.users || []);
          
          if (!users || users.length === 0) {
            console.warn("⚠️ Nenhum usuário retornado do backend");
          }
          
          const normalizedUsers = users.map(u => {
            // Mapear primary_role para categoria Radar e tipo
            const roleMapping = {
              'athlete': { type: 'ATHLETE', category: 'FIELD_POOL', roleLabel: 'Atleta' },
              'coach': { type: 'COACH', category: 'FIELD_POOL', roleLabel: 'Técnico & Treinador' },
              'health': { type: 'HEALTH', category: 'PERFORMANCE', roleLabel: 'Saúde & Performance' },
              'management': { type: 'MANAGEMENT', category: 'BUSINESS', roleLabel: 'Gestão & Operações' },
              'data': { type: 'DATA', category: 'BUSINESS', roleLabel: 'Dados & Tecnologia' },
              'media': { type: 'MEDIA', category: 'MEDIA', roleLabel: 'Conteúdo & Mídia' },
              'official': { type: 'OFFICIAL', category: 'FIELD_POOL', roleLabel: 'Arbitragem' },
              'education': { type: 'EDUCATION', category: 'BUSINESS', roleLabel: 'Educação' },
              'industry': { type: 'INDUSTRY', category: 'BUSINESS', roleLabel: 'Indústria' },
              'community': { type: 'COMMUNITY', category: 'BUSINESS', roleLabel: 'Comunidade Arena' }
            };

            const mapping = roleMapping[u.primary_role] || { type: 'COMMUNITY', category: 'BUSINESS', roleLabel: 'Membro' };
            
            return {
              id: `user-${u.user_id}`,
              name: u.full_name || u.display_name || u.email?.split('@')[0] || 'Usuário',
              role: u.role_subtype ? `${mapping.roleLabel} • ${u.role_subtype}` : mapping.roleLabel,
              position: mapping.roleLabel,
              sport: u.primary_sport || 'Multi-esporte',
              team: u.organization_name || 'Independente',
              teamLogo: null,
              nationality: u.country || 'Brasil',
              location: u.city ? `${u.city}, ${u.country || 'BR'}` : (u.country || 'Brasil'),
              avatar: u.avatar_url || null,
              type: mapping.type,
              category: mapping.category,
              entity_kind: 'community_user', // 🔒 Routing explícito
              followStatus: 'NONE', // TODO: vir do backend
              badges: u.is_verified ? ['VERIFICADO'] : [],
              connectionContext: null // TODO: calcular no backend
            };
          });
          allPeople.push(...normalizedUsers);
        } catch (e) { 
          console.error("❌ Erro ao buscar usuários do diretório:", e);
          console.warn("⚠️ Continuando sem usuários cadastrados. Mostrando apenas atletas.");
          // Não quebra a página - continua com atletas apenas
        }

        // Ordenar por relevância (verificados primeiro, depois alfabético)
        setPeople(allPeople.sort((a, b) => {
          if (a.badges?.includes('VERIFICADO') && !b.badges?.includes('VERIFICADO')) return -1;
          if (!a.badges?.includes('VERIFICADO') && b.badges?.includes('VERIFICADO')) return 1;
          return a.name.localeCompare(b.name);
        }));

      } catch (error) {
        console.error("Erro geral no diretório:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchAllData();
  }, []);

  // --- FILTRAGEM LOCAL (Radar) ---
  const filteredPeople = useMemo(() => {
    return people.filter(person => {
      // 1. Filtro de Texto
      const matchesSearch = person.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                            person.team?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                            person.role?.toLowerCase().includes(searchQuery.toLowerCase());

      // 2. Filtro de Categoria Radar (Nível 1)
      const matchesCategory = activeTab === 'ALL' || person.category === activeTab;

      // 3. Filtro de Esporte
      const matchesSport = selectedSport === 'ALL' || 
                           (selectedSport === 'SWIMMING' && person.sport === 'Natação') ||
                           (selectedSport === 'SOCCER' && person.sport === 'Futebol');

      return matchesSearch && matchesCategory && matchesSport;
    });
  }, [people, searchQuery, activeTab, selectedSport]);

  return (
    <PageShell>      
      {/* 1. HEADER E CONTROLES */}
      <div className="max-w-7xl mx-auto space-y-6">
        
        {/* Header Oficial */}
        <PageHeader
          title={t.pageTitle}
          subtitle={t.pageSubtitle}
          badgeText={t.pageBadge}
        />

        {/* Barra de Busca e Filtros Principais */}
        <div className="bg-white p-4 rounded-xl border border-gray-200 shadow-sm flex flex-col md:flex-row gap-4 items-center">
          
          {/* Input de Busca */}
          <div className="relative flex-1 w-full">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
            <input 
              type="text"
              placeholder={t.searchPlaceholder}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2.5 bg-gray-50 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-700/20 focus:border-green-700 transition-all"
            />
          </div>

          {/* Filtros Dropdown (Esporte) */}
          <select 
            value={selectedSport}
            onChange={(e) => setSelectedSport(e.target.value)}
            className="w-full md:w-48 px-4 py-2.5 bg-white border border-gray-200 rounded-lg text-gray-700 font-medium focus:outline-none focus:border-green-700 cursor-pointer"
          >
            <option value="ALL">{t.allSports}</option>
            <option value="SWIMMING">{t.swimming}</option>
            <option value="SOCCER">{t.football}</option>
          </select>
        </div>

        {/* Radar: Categorias de Descoberta (Nível 1) */}
        <div className="space-y-3">
          <h3 className="text-sm font-bold text-gray-700 uppercase tracking-wide">
            {t.radarTitle}
          </h3>
          <div className="flex items-center gap-3">
            {[
              { id: 'ALL', label: t.tabAll, icon: Users, description: 'Todo o ecossistema' },
              { id: 'FIELD_POOL', label: t.tabField, icon: Trophy, description: 'Atletas, técnicos, arbitragem' },
              { id: 'PERFORMANCE', label: t.tabPerformance, icon: Heart, description: 'Saúde, preparação, fisiologia' },
              { id: 'BUSINESS', label: t.tabBusiness, icon: Briefcase, description: 'Gestão, dados, educação' },
              { id: 'MEDIA', label: t.tabMedia, icon: Camera, description: 'Conteúdo e imprensa' },
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`
                  flex-1 flex flex-col items-center gap-1 px-4 py-3 rounded-xl text-sm font-bold transition-all
                  ${activeTab === tab.id 
                    ? 'bg-gradient-to-br from-green-50 to-green-100 text-[#111111] border-2 border-[#111111] shadow-sm' 
                    : 'bg-white text-gray-600 hover:bg-gray-50 border-2 border-gray-200'}
                `}
              >
                <tab.icon size={20} />
                <span className="text-xs">{tab.label}</span>
              </button>
            ))}
          </div>
          <p className="text-xs text-gray-500 italic">
            {t.radarTip}
          </p>
        </div>
      </div>

      {/* 2. GRID DE RESULTADOS */}
      <div className="max-w-7xl mx-auto">
        {loading ? (
          // Skeleton Loading
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {[1, 2, 3, 4, 5, 6, 7, 8].map((i) => (
              <div key={i} className="bg-white h-72 rounded-xl border border-gray-200 animate-pulse"></div>
            ))}
          </div>
        ) : filteredPeople.length > 0 ? (
          // Resultados
          <>
            <p className="text-sm text-gray-500 mb-4">
              {t.foundCount(filteredPeople.length)}
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {filteredPeople.map((person) => (
                <PersonCard key={`${person.sport}-${person.id}`} person={person} />
              ))}
            </div>
          </>
        ) : (
          // Empty State
          <div className="text-center py-20">
            <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Filter className="text-gray-400" size={32} />
            </div>
            <h3 className="text-lg font-bold text-gray-900">{t.emptyTitle}</h3>
            <p className="text-gray-500">{t.emptyDesc}</p>
            <button 
              onClick={() => { setSearchQuery(''); setSelectedSport('ALL'); setActiveTab('ALL'); }}
              className="mt-4 text-[#111111] font-bold hover:underline"
            >
              {t.clearFilters}
            </button>
          </div>
        )}
      </div>

    </PageShell>
  );
};

export default PeopleDirectory;
